import React, { useState } from "react";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import Button from "../../Common/Button";
import { setSnackBar } from "../../../store/common/commonSlice";
import {
    useAdjustAffiliateWalletMutation,
    useGetAffiliateQuery,
    useSetAffiliateStatusMutation,
} from "../../../store/shopAdmin/shopAdminApis";
import { handleDateTimeDefault } from "../../../helpers/utils";
import { COMMISSION_STATUS, WALLET_TX, rupees } from "./affiliateUtils";

const Empty = ({ text }) => (
    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
        {text}
    </Typography>
);

const AdjustWallet = ({ id, balance }) => {
    const dispatch = useDispatch();
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [adjust, { isLoading }] = useAdjustAffiliateWalletMutation();

    const submit = async () => {
        const value = Number(amount);
        if (!value)
            return dispatch(
                setSnackBar({ open: true, message: "Enter an amount (use minus to deduct)", severity: "error" }),
            );
        if (note.trim().length < 3)
            return dispatch(setSnackBar({ open: true, message: "Enter a reason", severity: "error" }));
        try {
            const r = await adjust({ id, amount: value, note: note.trim() }).unwrap();
            dispatch(
                setSnackBar({
                    open: true,
                    message: `Wallet adjusted. New balance ${rupees(r?.result?.balance)}`,
                    severity: "success",
                }),
            );
            setAmount("");
            setNote("");
        } catch (e) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: e?.data?.message || "Could not adjust the wallet",
                    severity: "error",
                }),
            );
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700}>
                Manual adjustment
            </Typography>
            <Typography variant="caption" color="text.secondary">
                Adds to or deducts from the wallet (now {rupees(balance)}). Saved in the wallet history and the audit
                log.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
                <TextField
                    size="small"
                    type="number"
                    label="Amount ₹ (−100 to deduct)"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    sx={{ width: { sm: 220 } }}
                />
                <TextField
                    size="small"
                    label="Reason"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    sx={{ flex: 1 }}
                    inputProps={{ maxLength: 255 }}
                />
                <Button fullWidth={false} variant="contained" onClick={submit} loading={isLoading}>
                    Apply
                </Button>
            </Stack>
        </Paper>
    );
};

const AffiliateDetailsDialog = ({ affiliateId, onClose }) => {
    const dispatch = useDispatch();
    const [tab, setTab] = useState("rewards");
    const { data, isFetching, error } = useGetAffiliateQuery(affiliateId, { skip: !affiliateId });
    const [setStatus, { isLoading: savingStatus }] = useSetAffiliateStatusMutation();

    const toggleBlock = async () => {
        const next = data.customer.affiliate_blocked ? "active" : "blocked";
        if (
            next === "blocked" &&
            !window.confirm(
                "Stop referral rewards for this customer? They can still log in and shop. Rewards already earned stay as they are.",
            )
        )
            return;
        try {
            await setStatus({ id: affiliateId, status: next }).unwrap();
            dispatch(
                setSnackBar({
                    open: true,
                    message:
                        next === "blocked"
                            ? "Referral rewards stopped for this customer"
                            : "Referral rewards turned back on",
                    severity: "success",
                }),
            );
        } catch (e) {
            dispatch(setSnackBar({ open: true, message: e?.data?.message || "Could not update", severity: "error" }));
        }
    };

    const c = data?.customer;
    return (
        <Dialog open={Boolean(affiliateId)} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                {c ? (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <span>{c.name}</span>
                        <Chip size="small" label={c.referral_code} variant="outlined" />
                        {c.affiliate_blocked ? <Chip size="small" color="error" label="Referrals blocked" /> : null}
                        {c.status === "blocked" ? (
                            <Chip size="small" color="error" variant="outlined" label="Account blocked" />
                        ) : null}
                    </Stack>
                ) : (
                    "Affiliate"
                )}
                <IconButton onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {isFetching && !data ? <CircularProgress /> : null}
                {error ? <Alert severity="error">{error?.data?.message || "Failed to load affiliate"}</Alert> : null}
                {data ? (
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            +91 {c.mobile}
                            {c.email ? ` · ${c.email}` : ""} · joined {handleDateTimeDefault(c.created_at)}
                        </Typography>
                        {data.flags.length ? (
                            <Alert severity="warning">
                                <strong>Check this affiliate:</strong> {data.flags.map((f) => f.label).join(" · ")}
                            </Alert>
                        ) : null}
                        <Grid container spacing={2}>
                            {[
                                ["In wallet", rupees(data.wallet.balance), "success.main"],
                                ["Pending", rupees(data.wallet.pending), "warning.main"],
                                ["Earned (lifetime)", rupees(data.wallet.lifetime_earned), "text.primary"],
                                ["Referred customers", data.referrals.length, "info.main"],
                            ].map(([label, value, color]) => (
                                <Grid item xs={6} md={3} key={label}>
                                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: "center" }}>
                                        <Typography variant="h6" color={color}>
                                            {value}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {label}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>

                        <Tabs
                            value={tab}
                            onChange={(_, v) => setTab(v)}
                            sx={{ borderBottom: 1, borderColor: "divider" }}
                        >
                            <Tab value="rewards" label={`Rewards (${data.commissions.length})`} />
                            <Tab value="referrals" label={`Referred customers (${data.referrals.length})`} />
                            <Tab value="wallet" label={`Wallet history (${data.transactions.length})`} />
                            <Tab value="audit" label="Audit" />
                        </Tabs>

                        {tab === "rewards" ? (
                            data.commissions.length ? (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Order</TableCell>
                                            <TableCell>Customer</TableCell>
                                            <TableCell align="right">Order amount</TableCell>
                                            <TableCell align="right">Reward</TableCell>
                                            <TableCell align="center">Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.commissions.map((r) => (
                                            <TableRow key={r.id}>
                                                <TableCell>{handleDateTimeDefault(r.created_at)}</TableCell>
                                                <TableCell>
                                                    {r.order_number}
                                                    <Typography
                                                        variant="caption"
                                                        display="block"
                                                        color="text.secondary"
                                                    >
                                                        {r.order_status}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {r.customer?.name}
                                                    <Typography
                                                        variant="caption"
                                                        display="block"
                                                        color="text.secondary"
                                                    >
                                                        {r.customer?.mobile}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">{rupees(r.order_amount)}</TableCell>
                                                <TableCell align="right">
                                                    <strong>{rupees(r.amount)}</strong>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        size="small"
                                                        {...(COMMISSION_STATUS[r.status] || { label: r.status })}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Empty text="No rewards yet" />
                            )
                        ) : null}

                        {tab === "referrals" ? (
                            data.referrals.length ? (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Customer</TableCell>
                                            <TableCell>Joined</TableCell>
                                            <TableCell>Came from</TableCell>
                                            <TableCell align="right">Paid orders</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.referrals.map((r) => (
                                            <TableRow key={r.id}>
                                                <TableCell>
                                                    {r.name}
                                                    <Typography
                                                        variant="caption"
                                                        display="block"
                                                        color="text.secondary"
                                                    >
                                                        {r.mobile}
                                                    </Typography>
                                                    {r.same_device ? (
                                                        <Chip
                                                            size="small"
                                                            color="warning"
                                                            label="Same phone as affiliate"
                                                            sx={{ mt: 0.5 }}
                                                        />
                                                    ) : null}
                                                </TableCell>
                                                <TableCell>{handleDateTimeDefault(r.created_at)}</TableCell>
                                                <TableCell>{r.referral_source || "-"}</TableCell>
                                                <TableCell align="right">{r.orders}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Empty text="Nobody has joined with this code yet" />
                            )
                        ) : null}

                        {tab === "wallet" ? (
                            <Stack spacing={2}>
                                <AdjustWallet id={affiliateId} balance={data.wallet.balance} />
                                {data.transactions.length ? (
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Note</TableCell>
                                                <TableCell align="right">Amount</TableCell>
                                                <TableCell align="right">Balance after</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.transactions.map((t) => (
                                                <TableRow key={t.id}>
                                                    <TableCell>{handleDateTimeDefault(t.created_at)}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            {...(WALLET_TX[t.type] || { label: t.type })}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{t.note}</TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            color: t.amount < 0 ? "error.main" : "success.main",
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {t.amount < 0 ? "−" : "+"}
                                                        {rupees(Math.abs(t.amount))}
                                                    </TableCell>
                                                    <TableCell align="right">{rupees(t.balance_after)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Empty text="No wallet entries yet" />
                                )}
                            </Stack>
                        ) : null}

                        {tab === "audit" ? (
                            data.audit.length ? (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>What happened</TableCell>
                                            <TableCell>By</TableCell>
                                            <TableCell>Details</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.audit.map((a) => (
                                            <TableRow key={a.id}>
                                                <TableCell>{handleDateTimeDefault(a.created_at)}</TableCell>
                                                <TableCell>
                                                    {a.action.replace("affiliate.", "").replace(/_/g, " ")}
                                                </TableCell>
                                                <TableCell>{a.by || "System"}</TableCell>
                                                <TableCell>
                                                    {a.note ? `${a.note} ` : ""}
                                                    <Typography variant="caption" color="text.secondary">
                                                        {a.after_data ? JSON.stringify(a.after_data) : ""}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Empty text="Nothing logged yet" />
                            )
                        ) : null}

                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                fullWidth={false}
                                variant="outlined"
                                color={c.affiliate_blocked ? "primary" : "error"}
                                onClick={toggleBlock}
                                loading={savingStatus}
                            >
                                {c.affiliate_blocked ? "Turn referral rewards back on" : "Stop referral rewards"}
                            </Button>
                        </Box>
                    </Stack>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

export default AffiliateDetailsDialog;
